<template>
  <div>
    <v-card class="pa-4 my-4" outlined>
      <v-row class="ma-0" align="center">
        <v-col cols="12" md="5">
          <v-text-field
            v-model="searchText"
            prepend-inner-icon="mdi-magnify"
            placeholder="Buscar por nombre"
            outlined
            dense
            hide-details
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="6" md="2">
          <v-select
            v-model="usageFilter"
            :items="usageOptions"
            item-text="text"
            item-value="value"
            label="Uso"
            outlined
            dense
            hide-details
          ></v-select>
        </v-col>
      </v-row>
    </v-card>

    <v-data-table :headers="headers" :items="filteredServiceTypes" :loading="loadingData" sort-by="name" class="elevation-1">
      <template v-slot:item.requiredAttachments="{ item }">
        <div class="d-flex flex-wrap py-1">
          <template v-if="sortedAttachments(item).length">
            <v-chip v-for="attachment in sortedAttachments(item)" :key="attachment.id" small class="mr-1 mb-1">
              {{ attachment.name }}
            </v-chip>
          </template>
          <span v-else class="text-caption grey--text">Sin documentos</span>
        </div>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="mx-2"
              fab
              dark
              depressed
              x-small
              v-bind="attrs"
              v-on="on"
              color="orange"
              @click="openMaintainerDialog(item)"
            >
              <v-icon small dark>mdi-pencil</v-icon>
            </v-btn>
          </template>
          <span>Editar tipo</span>
        </v-tooltip>
        <v-tooltip top>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" class="d-inline-block">
              <v-btn
                class="mx-2"
                fab
                dark
                x-small
                depressed
                color="pink"
                :disabled="item.servicesCount > 0"
                @click="triggerDeleteModal(item)"
              >
                <v-icon small dark>mdi-delete-forever</v-icon>
              </v-btn>
            </div>
          </template>
          <span v-if="item.servicesCount > 0">
            No se puede eliminar: hay {{ item.servicesCount }} servicio(s) usando este tipo.
          </span>
          <span v-else>Eliminar</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showMaintainerDialog"
      min-width="600"
      max-width="900"
      min-height="400"
      max-height="900"
      @keydown.esc="closeMaintainerDialog"
    >
      <template>
        <service-type-maintainer
          :service-type="selectedServiceType"
          @update-list="onServiceTypeSaved"
          @close-dialog="closeMaintainerDialog"
        ></service-type-maintainer>
      </template>
    </v-dialog>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card class="pa-6">
        <v-card-title class="text-uppercase">
          <div>Eliminar tipo {{ selectedServiceType != null ? selectedServiceType.name : '' }}</div>
        </v-card-title>
        <hr class="my-3" />
        <v-card-text class="my-0 py-0">
          <p>¿Está seguro de que desea eliminar este tipo de servicio?</p>
        </v-card-text>
        <hr class="my-3" />
        <v-card-actions class="justify-end">
          <v-btn text color="primary" rounded @click="closeDeleteDialog">Cancelar</v-btn>
          <v-btn color="primary" rounded @click="deleteServiceType(selectedServiceType)">Sí, eliminar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000">
      {{ snackbarMessage }}
    </v-snackbar>
  </div>
</template>

<script>
import ServiceTypeMaintainer from './ServiceTypeMaintainer.vue';
import ServicesService from '../services/services.service';
import { utilsMixin } from '../mixins/utilsMixin';

export default {
  name: 'ServiceTypesTab',
  components: { ServiceTypeMaintainer },
  mixins: [utilsMixin],
  data() {
    return {
      loadingData: false,
      showMaintainerDialog: false,
      deleteDialog: false,
      selectedServiceType: null,
      serviceTypes: [],
      searchText: '',
      usageFilter: null,
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'red',
      usageOptions: [
        { text: 'Todos', value: null },
        { text: 'En uso', value: 'in-use' },
        { text: 'Sin uso', value: 'unused' }
      ],
      headers: [
        { text: 'Nombre', value: 'name', sortable: true, width: '200px' },
        { text: 'Documentos requeridos', value: 'requiredAttachments', sortable: false, width: 'auto' },
        { text: 'Servicios asociados', value: 'servicesCount', sortable: false, align: 'center', width: '160px' },
        { text: 'Acciones', value: 'actions', sortable: false, align: 'end' }
      ]
    };
  },
  computed: {
    filteredServiceTypes() {
      const search = (this.searchText || '').trim().toLowerCase();
      return this.serviceTypes.filter(type => {
        const matchesSearch = !search || (type.name || '').toLowerCase().includes(search);
        const matchesUsage =
          !this.usageFilter ||
          (this.usageFilter === 'in-use' ? type.servicesCount > 0 : type.servicesCount === 0);
        return matchesSearch && matchesUsage;
      });
    }
  },
  created() {
    this.getServiceTypes();
  },
  methods: {
    sortedAttachments(item) {
      return [...(item.requiredAttachments || [])].sort((a, b) => a.name.localeCompare(b.name));
    },
    async getServiceTypes() {
      this.loadingData = true;
      const res = await ServicesService.getServiceTypes();
      if (res.status === 'ok') {
        this.serviceTypes = res.data;
      } else {
        this.showErrorSnackbar('Error al cargar los tipos de servicio');
      }
      this.loadingData = false;
    },
    // Llamado desde el botón "+" compartido en Servicios.vue vía $refs cuando
    // el tab activo es "Tipos de Servicio".
    openMaintainerDialog(serviceType) {
      this.selectedServiceType = serviceType || null;
      this.showMaintainerDialog = true;
    },
    closeMaintainerDialog() {
      this.showMaintainerDialog = false;
      this.selectedServiceType = null;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.selectedServiceType = null;
    },
    onServiceTypeSaved() {
      this.showSuccessSnackbar('Tipo de servicio guardado correctamente');
      this.getServiceTypes();
      this.$emit('changed');
    },
    triggerDeleteModal(serviceType) {
      this.selectedServiceType = serviceType;
      this.deleteDialog = true;
    },
    async deleteServiceType(serviceType) {
      this.loadingData = true;
      const res = await ServicesService.deleteServiceType(serviceType.id);
      if (res.status === 'ok') {
        this.showSuccessSnackbar('Tipo de servicio eliminado correctamente');
        this.closeDeleteDialog();
        this.getServiceTypes();
        this.$emit('changed');
      } else {
        this.showErrorSnackbar(res.message || 'No se pudo eliminar el tipo de servicio');
        this.loadingData = false;
      }
    }
  }
};
</script>
